import { useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Card,
  Stack,
  Button,
  Select,
  MenuItem,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";

export default function Calendar({ events, handleEventClick }) {
  const calendarRef = useRef(null);
  const today = new Date();

  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [view, setView] = useState("dayGridMonth");
  const [title, setTitle] = useState("");

  const [selectedDate, setSelectedDate] = useState(
    today.toISOString().split("T")[0],
  );

  const api = () => calendarRef.current?.getApi();
  const goToDate = (m, y) => api()?.gotoDate(new Date(y, m, 1));
  const changeView = (v) => {
    setView(v);
    api()?.changeView(v);
  };

  return (
    <Card
      sx={{
        maxWidth: 1100,
        mx: "auto",
        p: 3,
        borderRadius: 3,
        boxShadow: 4,
        bgcolor: "#f9f9f9",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        mb={2}
        gap={1}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            onClick={() => api()?.prev()}
            sx={{ color: "rgb(35, 71, 159)", borderColor: "rgb(35, 71, 159)" }}
          >
            Prev
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => api()?.next()}
            sx={{ color: "rgb(35, 71, 159)", borderColor: "rgb(35, 71, 159)" }}
          >
            Next
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ backgroundColor: "rgb(35, 71, 159)", color: "#fff" }}
            onClick={() => {
              const now = new Date();
              api()?.today();
              setMonth(now.getMonth());
              setYear(now.getFullYear());
              setSelectedDate(now.toISOString().split("T")[0]);
            }}
          >
            Today
          </Button>
        </Stack>
        <Stack>
          <Typography fontWeight={700} sx={{ ml: 10 }} fontSize={20}>
            {title}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Select
            size="small"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              goToDate(e.target.value, year);
            }}
          >
            {[
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ].map((m, i) => (
              <MenuItem key={i} value={i}>
                {m}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              goToDate(month, e.target.value);
            }}
          >
            {Array.from({ length: 10 }, (_, i) => 2020 + i).map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>

          <Button
            size="small"
            variant={view === "dayGridMonth" ? "contained" : "outlined"}
            onClick={() => changeView("dayGridMonth")}
            sx={{
              backgroundColor:
                view === "dayGridMonth" ? "rgb(35, 71, 159)" : "inherit",
              color: view === "dayGridMonth" ? "#fff" : "rgb(35, 71, 159)",
              borderColor: "rgb(35, 71, 159)",
            }}
          >
            Month
          </Button>
          <Button
            size="small"
            variant={view === "timeGridWeek" ? "contained" : "outlined"}
            onClick={() => changeView("timeGridWeek")}
            sx={{
              backgroundColor:
                view === "timeGridWeek" ? "rgb(35, 71, 159)" : "inherit",
              color: view === "timeGridWeek" ? "#fff" : "rgb(35, 71, 159)",
              borderColor: "rgb(35, 71, 159)",
            }}
          >
            Week
          </Button>
          <Button
            size="small"
            variant={view === "timeGridDay" ? "contained" : "outlined"}
            onClick={() => changeView("timeGridDay")}
            sx={{
              backgroundColor:
                view === "timeGridDay" ? "rgb(35, 71, 159)" : "inherit",
              color: view === "timeGridDay" ? "#fff" : "rgb(35, 71, 159)",
              borderColor: "rgb(35, 71, 159)",
            }}
          >
            Day
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          "& .fc-scrollgrid": {
            borderRadius: 3,
            border: "1px solid #ddd",
            overflow: "hidden",
          },
          "& .fc-daygrid-day": {
            padding: "10px",
            border: "1px solid #f0f0f0",
            borderRadius: 2,
            transition: "background-color 0.1s",
          },
          "& .fc-day-today": {
            backgroundColor: "rgba(35, 71, 159, 0.1) !important",
            border: "1px solid rgb(35, 71, 159)",
          },
          "& .fc-daygrid-day-number": {
            fontWeight: 600,
            fontSize: "14px",
            marginBottom: "4px",
          },
          "& .fc-daygrid-day:hover": {
            backgroundColor: "#F5F5F5",
            cursor: "pointer",
          },
          "& .fc-highlight": {
            color: "#fff !important",
            border: "1px solid #1565c0",
            padding: 0,
            margin: 0,
          },
          "& .selected-date": {
            backgroundColor: "rgb(35, 71, 159) !important",
          },
          "& .selected-date .fc-daygrid-day-number": {
            color: "#fff",
            fontWeight: 700,
          },
          "& .fc-event": {
            padding: "6px 8px",
            borderRadius: "8px",
            fontWeight: 700,
            fontSize: "13px",
            fontFamily: "Inter, Roboto, sans-serif",
            boxShadow: "0px 1px 3px rgba(0,0,0,0.2)",
            transition: "transform 0.1s",
          },
          "& .fc-event:hover": { transform: "scale(1.03)" },
          "& .fc-day-past": {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
          "& .fc-day-past .fc-daygrid-day-number": {
            color: "#9e9e9e",
          },
        }}
      >
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          events={events}
          key={events.length}
          dayMaxEvents={true}
          selectable={true}
          expandRows={true}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          selectAllow={(selectInfo) => {
            return (
              selectInfo.startStr === selectInfo.endStr ||
              selectInfo.startStr ===
                new Date(selectInfo.end).toISOString().split("T")[0]
            );
          }}
          dateClick={(info) => setSelectedDate(info.dateStr)}
          dayCellClassNames={(arg) =>
            arg.dateStr === selectedDate ? ["selected-date"] : []
          }
          datesSet={(arg) => {
            setTitle(arg.view.title);
            const d = arg.view.currentStart;
            setMonth(d.getMonth());
            setYear(d.getFullYear());
          }}
          eventTimeFormat={{
            hour: "numeric",
            minute: undefined,
            meridiem: "short",
          }}
        />
      </Box>
    </Card>
  );
}

function renderEventContent(eventInfo) {
  const isAllDay = eventInfo.event.allDay;

  const start = eventInfo.event.start;
  const end = eventInfo.event.end;

  const formatTime = (date) =>
    date
      ? new Date(date).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {eventInfo.event.title}
          </Typography>

          {!isAllDay && (
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              {formatTime(start)} - {formatTime(end)}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          px: 0.5,
          py: 0.3,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {!isAllDay && (
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              lineHeight: 1,
              opacity: 0.8,
            }}
          >
            {formatTime(start)} - {formatTime(end)}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {eventInfo.event.title}
        </Typography>
      </Box>
    </Tooltip>
  );
}
